import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Toaster } from 'react-hot-toast';

// Context
import { AdminAuthProvider } from './context/AdminAuthContext';

// Components
import ProtectedRoute from './components/ProtectedRoute';

// Pages
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Accounts from './pages/Accounts';
import AccountDetail from './pages/AccountDetail';
import Websites from './pages/Websites';
import PreviewLauncher from './pages/PreviewLauncher';
import WebsiteDetail from './pages/WebsiteDetail';
import Subscriptions from './pages/Subscriptions';
import SubscriptionDetail from './pages/SubscriptionDetail';
import Bundles from './pages/Bundles';
import BundleDetail from './pages/BundleDetail';
import Integrations from './pages/Integrations';
import WizardSessions from './pages/WizardSessions';
import WizardSessionDetail from './pages/WizardSessionDetail';
import WebsiteAnalytics from './pages/WebsiteAnalytics';
import Blog from './pages/Blog';
import BlogArticleEditor from './pages/BlogArticleEditor';
import BlogCategories from './pages/BlogCategories';
import KnowledgeBase from './pages/KnowledgeBase';
import KnowledgeBaseEditor from './pages/KnowledgeBaseEditor';
import KnowledgeBaseCategories from './pages/KnowledgeBaseCategories';
import Affiliates from './pages/Affiliates';
import AffiliateDetail from './pages/AffiliateDetail';
import Commissions from './pages/Commissions';
import WebsiteCodeEditor from './pages/WebsiteCodeEditor';
import Jobs from './pages/Jobs';
import RiskDashboard from './pages/RiskDashboard';
import RiskUsers from './pages/RiskUsers';
import RiskUserDetail from './pages/RiskUserDetail';
import BlockedEntities from './pages/BlockedEntities';
import RiskAudit from './pages/RiskAudit';
import AICustomizationAudits from './pages/AICustomizationAudits';
import AICustomizationAuditDetail from './pages/AICustomizationAuditDetail';
import ReleaseNotes from './pages/ReleaseNotes';
import WhatsAppBroadcast from './pages/WhatsAppBroadcast';
import Discovery from './pages/Discovery';
import DiscoveryNew from './pages/DiscoveryNew';
import DiscoverySettings from './pages/DiscoverySettings';
import DiscoverySession from './pages/DiscoverySession';
import DiscoveryAgent from './pages/DiscoveryAgent';

function App() {
  return (
    <AdminAuthProvider>
      <Helmet>
        <title>Zappy Backoffice</title>
        <meta name="description" content="Zappy Admin Dashboard" />
      </Helmet>
      
      <Toaster
        position="top-center"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
          },
          success: {
            duration: 3000,
            iconTheme: {
              primary: '#4ade80',
              secondary: '#fff',
            },
          },
          error: {
            duration: 4000,
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff',
            },
          },
        }}
      />
      
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route 
          path="/dashboard" 
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/accounts" 
          element={
            <ProtectedRoute>
              <Accounts />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/accounts/:accountId" 
          element={
            <ProtectedRoute>
              <AccountDetail />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/websites" 
          element={
            <ProtectedRoute>
              <Websites />
            </ProtectedRoute>
          } 
        />
        <Route
          path="/open-editor/:websiteId"
          element={
            <ProtectedRoute>
              <PreviewLauncher />
            </ProtectedRoute>
          }
        />
        <Route 
          path="/websites/:websiteId" 
          element={
            <ProtectedRoute>
              <WebsiteDetail />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/websites/:websiteId/code" 
          element={
            <ProtectedRoute>
              <WebsiteCodeEditor />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/subscriptions" 
          element={
            <ProtectedRoute>
              <Subscriptions />
            </ProtectedRoute>
          } 
        />
        <Route
          path="/bundles"
          element={
            <ProtectedRoute>
              <Bundles />
            </ProtectedRoute>
          }
        />
        <Route
          path="/bundles/:accountId"
          element={
            <ProtectedRoute>
              <BundleDetail />
            </ProtectedRoute>
          }
        />
        <Route 
          path="/subscriptions/:subscriptionId" 
          element={
            <ProtectedRoute>
              <SubscriptionDetail />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/integrations" 
          element={
            <ProtectedRoute>
              <Integrations />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/wizard-sessions" 
          element={
            <ProtectedRoute>
              <WizardSessions />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/wizard-sessions/:sessionId" 
          element={
            <ProtectedRoute>
              <WizardSessionDetail />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/analytics" 
          element={
            <ProtectedRoute>
              <WebsiteAnalytics />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/blog" 
          element={
            <ProtectedRoute>
              <Blog />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/blog/new" 
          element={
            <ProtectedRoute>
              <BlogArticleEditor />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/blog/edit/:id" 
          element={
            <ProtectedRoute>
              <BlogArticleEditor />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/blog/categories" 
          element={
            <ProtectedRoute>
              <BlogCategories />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/knowledge-base" 
          element={
            <ProtectedRoute>
              <KnowledgeBase />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/knowledge-base/new" 
          element={
            <ProtectedRoute>
              <KnowledgeBaseEditor />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/knowledge-base/edit/:id" 
          element={
            <ProtectedRoute>
              <KnowledgeBaseEditor />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/knowledge-base/categories" 
          element={
            <ProtectedRoute>
              <KnowledgeBaseCategories />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/affiliates" 
          element={
            <ProtectedRoute>
              <Affiliates />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/affiliates/:id" 
          element={
            <ProtectedRoute>
              <AffiliateDetail />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/commissions" 
          element={
            <ProtectedRoute>
              <Commissions />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/jobs" 
          element={
            <ProtectedRoute>
              <Jobs />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/risk" 
          element={
            <ProtectedRoute>
              <RiskDashboard />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/risk/users" 
          element={
            <ProtectedRoute>
              <RiskUsers />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/risk/users/:userId" 
          element={
            <ProtectedRoute>
              <RiskUserDetail />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/risk/blocked" 
          element={
            <ProtectedRoute>
              <BlockedEntities />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/risk/audit" 
          element={
            <ProtectedRoute>
              <RiskAudit />
            </ProtectedRoute>
          } 
        />
        <Route
          path="/ai-customizations"
          element={
            <ProtectedRoute>
              <AICustomizationAudits />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ai-customizations/:id"
          element={
            <ProtectedRoute>
              <AICustomizationAuditDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="/whatsapp-broadcast"
          element={
            <ProtectedRoute>
              <WhatsAppBroadcast />
            </ProtectedRoute>
          }
        />
        <Route
          path="/discovery"
          element={
            <ProtectedRoute>
              <Discovery />
            </ProtectedRoute>
          }
        />
        <Route
          path="/discovery/new"
          element={
            <ProtectedRoute>
              <DiscoveryNew />
            </ProtectedRoute>
          }
        />
        <Route
          path="/discovery/settings"
          element={
            <ProtectedRoute>
              <DiscoverySettings />
            </ProtectedRoute>
          }
        />
        <Route
          path="/discovery/:sessionId/agents/:agentId"
          element={
            <ProtectedRoute>
              <DiscoveryAgent />
            </ProtectedRoute>
          }
        />
        <Route
          path="/discovery/:sessionId"
          element={
            <ProtectedRoute>
              <DiscoverySession />
            </ProtectedRoute>
          }
        />
        <Route 
          path="/release-notes" 
          element={
            <ProtectedRoute>
              <ReleaseNotes />
            </ProtectedRoute>
          } 
        />
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </AdminAuthProvider>
  );
}

export default App;

